import styled, { css } from 'styled-components';
import { Hymn, SongFeature } from '../../../common/src/model.ts';
import { formatVerses } from '../../../common/src/verses.ts';
import VocalsIcon from '../assets/vocals.svg?react';
import { EditSongButton } from '../util/edit_song.tsx';
import { nameBeforeSuffix, nameWithSuffixRow, suffixAfterName, suffixRoom } from './name_with_suffix.ts';

// two lines per hymn name rather than one with an ellipsis; the row height follows it (HymnRowHeight)
export const WrapHymnNames = false;

interface SongDisplayNameProps {
  hymn: Hymn;
  // the row is dimmed (gated, or not clickable): the verses take the row's color, like the title
  isDimmed: boolean;
  searchRegEx: RegExp | null;
  isInSongEditMode?: boolean;
}

// a hymn list row's name cell: the title with the part a search matched in bold, then the verses a weekly song list
// chose, the edit button in song edit mode and the vocals icon
export const SongDisplayName = ({ hymn, isDimmed, searchRegEx, isInSongEditMode }: SongDisplayNameProps) => {
  const displayName = hymn.title;

  const match = searchRegEx && displayName.match(searchRegEx);
  const startIndex = match ? displayName.search(searchRegEx) : -1;
  const endIndex = match ? startIndex + match[0].length : 0;

  const beforeBold = startIndex >= 0 ? displayName.slice(0, startIndex) : displayName;
  const bold = startIndex >= 0 ? displayName.slice(startIndex, endIndex) : '';
  const afterBold = startIndex >= 0 ? displayName.slice(endIndex) : '';
  // the verses a weekly song list chose, after the title and outside the search highlight (issue #4169)
  const versesText = formatVerses(hymn.verses);

  return (
    <SongNameWrapper>
      <SongNameInnerWrapper $hasVerses={!!versesText}>
        <SongNameInnerText $hasVerses={!!versesText}>
          <SongTitleText>
            {beforeBold}
            <b>{bold}</b>
            {afterBold}
          </SongTitleText>
          {versesText && <SongVerses $isDimmed={isDimmed}>({versesText})</SongVerses>}
        </SongNameInnerText>
        {isInSongEditMode && <EditSongButton songSlug={hymn.slug} />}
      </SongNameInnerWrapper>
      <VocalsIconWrapper $visible={hymn.features?.includes(SongFeature.Vocals)}>
        <VocalsIcon className="vocalsIcon" />
      </VocalsIconWrapper>
    </SongNameWrapper>
  );
};

const VocalsIconWrapper = styled.div<{ $visible?: boolean }>`
  display: ${(props) => (props.$visible ? 'inherit' : 'none')};
  flex-shrink: 0;
`;

const SongNameWrapper = styled.div`
  flex: 1;
  min-width: 0;
  padding-left: 7px;
  margin-right: 2px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SongNameInnerWrapper = styled.div<{ $hasVerses: boolean }>`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  ${
    // a row with verses is the room a long verses suffix takes two-thirds of (issue #4212), its width coming from the
    // row rather than the name; a row without verses keeps its usual layout
    (props) => (props.$hasVerses && !WrapHymnNames ? suffixRoom : '')
  }
`;

const SongNameInnerText = styled.div<{ $hasVerses: boolean }>`
  ${
    WrapHymnNames
      ? `
    text-wrap: wrap;

    // Limit wrap to two lines
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  `
      : `
    text-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `
  }
  ${
    // with verses the title alone takes the ellipsis, so a long title never clips the verses after it; the row shrinks
    // to its contents, so in song edit mode the edit button sits right after the verses
    (props) =>
      props.$hasVerses && !WrapHymnNames
        ? css`
            ${nameWithSuffixRow}
            align-items: baseline;
          `
        : ''
  }
`;

const SongTitleText = styled.span`
  ${nameBeforeSuffix}
`;

// on a dimmed row the verses take the row's dimmed color, like the title
const SongVerses = styled.span<{ $isDimmed: boolean }>`
  ${suffixAfterName}
  padding-left: 0.3em;
  font-size: 0.85em;
  color: ${(props) => (props.$isDimmed ? 'inherit' : 'var(--color-text-darker)')};
`;
